import { useEffect, useMemo, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PrimaryButton } from '@pulse-web-ui/button-v2';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import {
  type AgentBankDetailsFormParams,
  type UpdateProfileDraftRequestParams,
  profileSectionToAnalyticEvent,
  useUpdateProfileDraft,
} from '@entities/index';

import {
  AnalyticEvent,
  BoundaryErrorType,
  PROFILE,
  ProfileSection,
  addTestAttribute,
  getMismatches,
  removeSpaces,
  sendRequestErrorsAnalytics,
  useStores,
} from '@shared/index';

export interface SubmitBankDetailsDraftProps {
  isLoading?: boolean;
}

export const SubmitBankDetailsDraft = observer(
  ({ isLoading }: SubmitBankDetailsDraftProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { showBoundary } = useErrorBoundary();
    const {
      MainStore: {
        agentContractStore: { updateAgentProfileDraft, agentProfileDraft },
      },
    } = useStores();

    const {
      handleSubmit,
      formState: { errors },
    } = useFormContext<AgentBankDetailsFormParams>();

    const [requestParams, setRequestParams] =
      useState<UpdateProfileDraftRequestParams>({
        agentProfileChange: {
          ...agentProfileDraft,
          bankDetails: undefined,
        },
      });

    const {
      res,
      refetch: updateProfileDraft,
      error,
      isFetching,
    } = useUpdateProfileDraft(requestParams, [requestParams]);

    const isRequestParamsFilled = useMemo(
      () =>
        !!requestParams?.agentProfileChange?.bankDetails?.bic?.length &&
        !!requestParams?.agentProfileChange?.bankDetails?.nameBank?.length &&
        !!requestParams?.agentProfileChange?.bankDetails?.corAccount?.length &&
        !!requestParams?.agentProfileChange?.bankDetails?.bankAccount?.length,
      [requestParams]
    );

    useEffect(() => {
      if (isRequestParamsFilled) {
        sendAnalyticEvent(AnalyticEvent.AGENT_EDITPERSONALDATA_SAVE, {
          category: profileSectionToAnalyticEvent[ProfileSection.BANK_DETAILS],
        });

        updateProfileDraft();
      }
    }, [requestParams]);

    useEffect(() => {
      if (error) {
        sendRequestErrorsAnalytics(
          AnalyticEvent.AGENT_EDITPERSONALDATA_OPEN_ERROR,
          getMismatches(error)
        );
        showBoundary(BoundaryErrorType.SomethingWentWrong);
      }
    }, [error]);

    useEffect(() => {
      if (!isFetching && res && isRequestParamsFilled) {
        updateAgentProfileDraft(requestParams.agentProfileChange);
        navigate(PROFILE);
      }
    }, [requestParams, isFetching, res]);

    const submitPage = handleSubmit((values) => {
      const { bic, nameBank, bankAccount, corAccount } = values;

      setRequestParams((prevParams: UpdateProfileDraftRequestParams) => ({
        ...prevParams,
        agentProfileChange: {
          ...prevParams.agentProfileChange,
          bankDetails: {
            bic,
            nameBank,
            bankAccount: removeSpaces(bankAccount),
            corAccount: removeSpaces(corAccount),
          },
        },
      }));
    });

    const isDisabled = useMemo(
      () => !!Object.keys(errors).length || isLoading,
      [errors, isLoading]
    );

    return (
      <PrimaryButton
        size='l'
        onClick={submitPage}
        disabled={isDisabled}
        isLoading={isFetching}
        {...addTestAttribute('agentBankDetailsDraft.submitButton')}
      >
        {t('COMMON:buttons.save')}
      </PrimaryButton>
    );
  }
);
